/**
 * Submission document — Cosmos `submissions` container.
 *
 * Partition key: `/yearMonth` (e.g. "2026-05"). Bounded per-month queries.
 *
 * Phase M+3.5: Replaces the Power Automate pipeline (PLAN.md row inserted
 * between M+3 and M+4). A customer POST to /api/send now writes a
 * SubmissionDoc here and triggers a Microsoft Graph email — instead of
 * forwarding to an opaque Power Automate webhook + Microsoft Lists.
 *
 * Status state machine (sales pipeline):
 *
 *   new ──mark-quoted──▶ quoted ──mark-won──▶ won
 *                          │                    ▲
 *                          └─mark-lost──▶ lost ─┘
 *                                                ┊
 *                            (admin-only reopen) ┊
 *                          quoted ◀──── won ┘
 *                          quoted ◀──── lost
 *
 *   mark-* actions: admin OR editor
 *   reopen action:  admin only
 *
 * The state machine is enforced both as a Zod-validated runtime constant
 * (`SUBMISSION_STATUS_TRANSITIONS`) so the store, the API, and the admin UI
 * all agree on what's legal.
 *
 * Internal notes are append-only — there is no "edit note" or "delete note"
 * endpoint. The audit trail relies on this.
 */

import { z } from 'zod';
import {
  CosmosBaseSchema,
  IsoTimestampSchema,
  YearMonthSchema,
} from './common.js';
import { toYearMonth } from './audit.js';
import { MAX_LOGO_COUNT } from './logoLimits.js';

// ---------------------------------------------------------------------------
// Status + actions
// ---------------------------------------------------------------------------

export const SUBMISSION_STATUSES = ['new', 'quoted', 'won', 'lost'] as const;
export const SubmissionStatusSchema = z.enum(SUBMISSION_STATUSES);
export type SubmissionStatus = z.infer<typeof SubmissionStatusSchema>;

export const SUBMISSION_ACTIONS = [
  'mark-quoted',
  'mark-won',
  'mark-lost',
  'reopen',
] as const;
export const SubmissionActionSchema = z.enum(SUBMISSION_ACTIONS);
export type SubmissionAction = z.infer<typeof SubmissionActionSchema>;

/**
 * The roles authorized to perform a transition. `admin` and `editor` share
 * the bulk; only `admin` may `reopen` a terminal-but-not-really state.
 */
export type SubmissionTransitionRole = 'admin' | 'editor';

interface SubmissionTransitionRule {
  readonly action: SubmissionAction;
  readonly to: SubmissionStatus;
  readonly roles: ReadonlyArray<SubmissionTransitionRole>;
}

/**
 * Allowed transitions per source state. Empty array = terminal (no actions
 * legal from that state). Kept as a runtime constant so callers can:
 *   - render available actions in the admin UI
 *   - assert it in tests
 *   - enforce it in the store
 */
export const SUBMISSION_STATUS_TRANSITIONS: Readonly<
  Record<SubmissionStatus, ReadonlyArray<SubmissionTransitionRule>>
> = {
  new: [{ action: 'mark-quoted', to: 'quoted', roles: ['admin', 'editor'] }],
  quoted: [
    { action: 'mark-won', to: 'won', roles: ['admin', 'editor'] },
    { action: 'mark-lost', to: 'lost', roles: ['admin', 'editor'] },
  ],
  won: [{ action: 'reopen', to: 'quoted', roles: ['admin'] }],
  lost: [{ action: 'reopen', to: 'quoted', roles: ['admin'] }],
};

/**
 * Returns true iff `action` is legal from `from` for `role`.
 * Used by both the store (enforce) and the UI (render).
 */
export function isLegalSubmissionTransition(
  from: SubmissionStatus,
  action: SubmissionAction,
  role: SubmissionTransitionRole,
): boolean {
  if (role !== 'admin' && role !== 'editor') return false;
  const rules = SUBMISSION_STATUS_TRANSITIONS[from];
  if (!rules) return false;
  return rules.some((r) => r.action === action && r.roles.includes(role));
}

/**
 * Resolve the target status for an action from the given starting state.
 * Throws on an illegal pairing — callers should pre-validate with
 * `isLegalSubmissionTransition` when displaying available actions.
 *
 * Role is checked separately by the store (`reassignSubmission` /
 * `updateSubmissionStatus` enforce). This helper only resolves the next
 * state, independent of role.
 */
export function applySubmissionStatusAction(
  from: SubmissionStatus,
  action: SubmissionAction,
): SubmissionStatus {
  const rules = SUBMISSION_STATUS_TRANSITIONS[from] ?? [];
  const match = rules.find((r) => r.action === action);
  if (!match) {
    throw new Error(
      `Illegal submission transition: ${from} cannot ${action}`,
    );
  }
  return match.to;
}

// ---------------------------------------------------------------------------
// Internal notes (append-only)
// ---------------------------------------------------------------------------

export const SubmissionNoteSchema = z
  .object({
    /** uuid-ish id, unique within the submission. */
    id: z.string().min(1).max(128),
    /** Author's email. The sales user who wrote the note. */
    author: z.string().email(),
    /** Markdown-allowed note text. */
    text: z.string().min(1).max(4000),
    /** ISO 8601 timestamp. */
    createdAt: IsoTimestampSchema,
  })
  .strict();
export type SubmissionNote = z.infer<typeof SubmissionNoteSchema>;

// ---------------------------------------------------------------------------
// Order payload — mirrors apps/public/src/types.ts SubmissionData
// ---------------------------------------------------------------------------

/**
 * Free-form per-item record. We accept any string keys plus optional
 * customText so future flow shapes don't require schema changes.
 *
 * Bounded by .max() on every field to prevent oversized rows from sneaking
 * through.
 */
const SubmissionItemSchema = z
  .object({
    id: z.string().min(1).max(256),
    label: z.string().min(1).max(512),
    customText: z.string().max(2000).optional(),
  })
  .strict();

const SubmissionOrderBoxSchema = z
  .object({
    themeId: z.string().min(1).max(128),
    // Admin-chosen display name for the box (e.g. "SPRING BOX"). OPTIONAL so
    // legacy orders written before this field existed still validate. `themeId`
    // remains the slug/id; this is the human-facing name shown on orders.
    boxName: z.string().min(1).max(256).optional(),
    packagingId: z.string().min(1).max(128),
    packagingOther: z.string().max(2000).optional(),
    merchIds: z.array(z.string().min(1).max(128)).max(50),
    merchOther: z.string().max(2000).optional(),
    finalTouchIds: z.array(z.string().min(1).max(128)).max(50),
    finalTouchOther: z.string().max(2000).optional(),
  })
  .strict();

const SubmissionPricingSchema = z
  .object({
    unit: z.number().nonnegative(),
    discountPct: z.number().nonnegative().max(100),
    total: z.number().nonnegative(),
    currency: z.string().min(1).max(8),
  })
  .strict();

const LogoBlobPointerSchema = z
  .object({
    container: z.string().min(1).max(63),
    path: z.string().min(1).max(512),
    contentType: z.string().min(1).max(128),
    sizeBytes: z.number().int().nonnegative(),
  })
  .strict();

/**
 * Chapter 10 (F15) — ONE of a company's logos.
 *
 * `blob` is optional because the upload is all-or-nothing and the order may
 * still be sent when the bytes could not be stored: the filenames are what the
 * sales team works from, and the customer emails the artwork separately.
 */
const SubmissionLogoSchema = z
  .object({
    filename: z.string().min(1).max(512),
    sizeKB: z.number().int().nonnegative(),
    blob: LogoBlobPointerSchema.optional(),
  })
  .strict();

const SubmissionDesignSchema = z
  .object({
    logoProvided: z.boolean(),
    filename: z.string().max(512).optional(),
    sizeKB: z.number().int().nonnegative().optional(),
    // M1 (customer-logo pipeline): pointer to the stored logo blob in the
    // PRIVATE `submission-logos` container (publicAccess:'None'). Optional +
    // additive + strict so legacy submission docs (which only ever carried
    // logoProvided/filename/sizeKB, never the bytes) keep validating with no
    // migration. The blob is reached only via a signed download URL minted
    // at request time (M3) — the pointer itself is NOT a public URL.
    //
    // Chapter 10 (F15): for a multi-logo order this mirrors the FIRST logo, so
    // every reader written against the single-logo shape keeps working. The
    // full set is in `logos`.
    logoBlob: LogoBlobPointerSchema.optional(),
    /**
     * Chapter 10 (F15) — every logo the customer attached, in the order they
     * picked them. Additive and optional: every document already in Cosmos
     * carries its single logo in the fields above and needs no migration.
     * Readers should go through `submissionLogos`, which normalises both
     * shapes.
     */
    logos: z.array(SubmissionLogoSchema).max(MAX_LOGO_COUNT).optional(),
  })
  .strict();
export type SubmissionDesign = z.infer<typeof SubmissionDesignSchema>;
export type SubmissionLogo = z.infer<typeof SubmissionLogoSchema>;
export type LogoBlobPointer = z.infer<typeof LogoBlobPointerSchema>;

/**
 * The subset of a logo pointer needed to find a blob for read or delete.
 * Container comes off the POINTER (the source of truth), never off a config
 * default, so a doc pointing at a renamed container still resolves.
 */
export interface LogoBlobLocation {
  readonly container: string;
  readonly path: string;
}

/**
 * Every logo on an order, normalised — the ONE reader of "one logo or many?".
 *
 * Chapter 10 (F15). Four consumers need this answer: the admin submission
 * detail view, the CSV export, the XLSX export and the GDPR erasure path. Each
 * re-deriving the legacy fallback is how a right-to-erasure request ends up
 * deleting logo 1 and leaving logos 2-4 in blob storage.
 *
 *   - `design.logos` present → that array, verbatim.
 *   - otherwise, a legacy single-logo order → one entry synthesised from
 *     `filename` / `sizeKB` / `logoBlob`.
 *   - no logo → empty.
 *
 * `logoProvided` stays the flag that decides whether a logo exists at all,
 * because that is what every existing reader branched on.
 */
export function submissionLogos(design: SubmissionDesign): SubmissionLogo[] {
  if (design.logos && design.logos.length > 0) return [...design.logos];
  if (!design.logoProvided) return [];
  return [
    {
      filename: design.filename ?? '',
      sizeKB: design.sizeKB ?? 0,
      ...(design.logoBlob ? { blob: design.logoBlob } : {}),
    },
  ];
}

/**
 * Every stored blob belonging to an order, de-duplicated — what the GDPR
 * erasure path must delete before the document goes.
 *
 * A new multi-logo submission mirrors its first logo's pointer into
 * `design.logoBlob` for legacy readers AND lists it in `design.logos`; that is
 * ONE blob, so it is returned once. The legacy pointer is still checked
 * separately (and appended when no entry mentions it) because an unreferenced
 * pointer is untracked PII either way.
 */
export function submissionLogoBlobs(
  design: SubmissionDesign,
): LogoBlobLocation[] {
  const out: LogoBlobLocation[] = [];
  const seen = new Set<string>();
  // The parameter is loose (`container?`/`path?`) because `apps/admin` compiles
  // with `strict: false`, under which Zod's inferred output type marks every
  // field optional. A pointer missing either half is unusable anyway, so the
  // guard is real rather than a type appeasement.
  const add = (
    blob: { container?: string; path?: string } | undefined,
  ): void => {
    if (!blob || !blob.container || !blob.path) return;
    const key = `${blob.container}/${blob.path}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ container: blob.container, path: blob.path });
  };
  for (const logo of design.logos ?? []) add(logo.blob);
  add(design.logoBlob);
  return out;
}

const SubmissionItemsSchema = z
  .object({
    packaging: SubmissionItemSchema,
    merch: z.array(SubmissionItemSchema).max(50),
    finalTouch: z.array(SubmissionItemSchema).max(50),
  })
  .strict();

/**
 * N2b — generic, self-describing per-step selection record.
 *
 * The legacy `box.{packagingId,merchIds,finalTouchIds}` + `items.*` fields
 * are a *positional projection* of the first three option steps (slot 0 =
 * packaging, 1 = merch, 2 = final-touch). That projection is byte-identical
 * for the canonical Spring/Office archetype but is structurally lossy for a
 * box authored with novel step ids (e.g. `vessel`/`treats`/`wrap`) or with
 * more than three option steps.
 *
 * `SubmissionOrderStepSchema` records EVERY option step's selections keyed by
 * its real `stepId` + human `title`, so the admin /submissions UI can render
 * an arbitrary box structure faithfully. Each item carries the optional
 * `customText` of a first-class "Other…" (`OptionSchema.isOther`) selection.
 *
 * Optional + additive: legacy submission docs (written before N2b) have no
 * `steps` field and validate unchanged. The public bundle populates BOTH the
 * legacy projection and this generic array on every new submission.
 */
export const SubmissionOrderStepSchema = z
  .object({
    stepId: z.string().min(1).max(128),
    title: z.string().min(1).max(256),
    items: z.array(SubmissionItemSchema).max(100),
  })
  .strict();
export type SubmissionOrderStep = z.infer<typeof SubmissionOrderStepSchema>;

const SubmissionSummarySchema = z
  .object({
    customer: z.string().max(1024),
    email: z.string().max(512),
    phone: z.string().max(64),
    quantity: z.string().max(32),
    theme: z.string().max(128),
    packaging: z.string().max(2048),
    merch: z.string().max(4096),
    finalTouch: z.string().max(2048),
    deliveryDate: z.string().max(64),
    logistics: z.string().max(1024),
    totalPrice: z.string().max(64),
    /**
     * The fully-rendered email body (plain text). Stored verbatim so the
     * admin UI can show the customer exactly what was sent.
     */
    emailContent: z.string().max(20000),
  })
  .strict();

const SubmissionOrderSchema = z
  .object({
    box: SubmissionOrderBoxSchema,
    /** Bounded high — orders > 100k boxes don't happen in practice. */
    quantity: z.number().int().nonnegative().max(100_000),
    pricing: SubmissionPricingSchema,
    storage: z.string().min(1).max(512),
    shipment: z.string().min(1).max(512),
    deliveryDate: z.string().max(64).optional(),
    notes: z.string().max(4000).optional(),
    design: SubmissionDesignSchema,
    items: SubmissionItemsSchema,
    summary: SubmissionSummarySchema,
    /**
     * N2b — optional generic per-step selections (every option step keyed by
     * its real id). Populated for all new submissions; absent on legacy docs.
     * The legacy `box`/`items` fields remain the source of truth for the
     * canonical archetype projection and the email render.
     */
    steps: z.array(SubmissionOrderStepSchema).max(50).optional(),
  })
  .strict();

const SubmissionCustomerSchema = z
  .object({
    fullName: z.string().min(1).max(256),
    email: z.string().email().max(512),
    phone: z.string().max(64).optional(),
    company: z.string().min(1).max(512),
  })
  .strict();

const SubmissionConsentSchema = z
  .object({
    termsAccepted: z.boolean(),
    newsletter: z.boolean(),
  })
  .strict();

// ---------------------------------------------------------------------------
// SubmissionDoc — top-level
// ---------------------------------------------------------------------------

export const SubmissionDocSchema = CosmosBaseSchema.extend({
  docType: z.literal('submission'),
  /** Partition key — must equal YYYY-MM of `createdAt`. */
  yearMonth: YearMonthSchema,
  /** Which flow the customer used (e.g. "spring", "office"). */
  flowId: z.string().min(1).max(128),

  customer: SubmissionCustomerSchema,
  order: SubmissionOrderSchema,

  // Sales pipeline state
  status: SubmissionStatusSchema,
  /** Optional assignee email. Null/missing = unassigned. */
  assignedTo: z.string().email().max(512).optional(),
  /** Append-only sales notes. */
  internalNotes: z.array(SubmissionNoteSchema).max(500),

  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
  /**
   * Did the Graph sendMail call succeed? If false and `emailError` is set,
   * operators can diagnose via apps/admin/docs/runbooks/email-bouncing.md.
   */
  emailSent: z.boolean(),
  emailError: z.string().max(2000).optional(),

  consent: SubmissionConsentSchema,
})
  .strict()
  .refine((v) => v.yearMonth === toYearMonth(v.createdAt), {
    message: 'yearMonth must match the YYYY-MM of `createdAt`',
    path: ['yearMonth'],
  });
export type SubmissionDoc = z.infer<typeof SubmissionDocSchema>;

// ---------------------------------------------------------------------------
// Input shape for createSubmission (no server-stamped fields)
// ---------------------------------------------------------------------------

/**
 * What /api/send hands to the store. The store fills in id, yearMonth,
 * createdAt, updatedAt, status='new', emailSent=false, internalNotes=[].
 */
export const NewSubmissionInputSchema = z
  .object({
    flowId: z.string().min(1).max(128),
    customer: SubmissionCustomerSchema,
    order: SubmissionOrderSchema,
    consent: SubmissionConsentSchema,
  })
  .strict();
export type NewSubmissionInput = z.infer<typeof NewSubmissionInputSchema>;
